import type { BallState, MatchState, PlayerState } from './state';
import type { SimConfig } from '../config/simConfig';
import type { SimEvent } from './events';
import type { EntityId, TeamIdx } from '../types';
import { PITCH } from '../config/pitchConfig';
import { BTN } from './input';
import { pickPassTarget } from './receiver';
import { passChargeFor } from './passing';
import { solveAerialKick } from './aerial';
import { pickLongTarget } from './longPass';
import { inCeremony } from '../rules/phase';

/** A kick queued by `applyActions` on release, applied to the ball after integration (§5.3/§5.4). */
export interface KickRequest {
  entity: EntityId;
  team: TeamIdx;
  /** The kick button bit (PASS/THROUGH/SHOOT). D110: may additionally carry BTN.LOFT — the hold-modifier
   *  that turns this kick into a chip/cross. No new field: `applyActions` copies the modifier out of the
   *  live button word alongside the kick bit. */
  button: number;
  chargeTicks: number;
  aimYaw: number;
  aimElev: number; // 0..1 look-up loft input (D18); elevation += aimElev × ELEV_GAIN
  assist?: boolean; // D46: false ⇒ skip the D34 pass soft-lock (keeper distributions aim exactly where the AI chose — no own-goal bend). Defaults true.
  /** D113: exact launch speed (m/s), overriding the charge curve. An aerial strike is not a charged
   *  kick — a volley/header has its own force table (§17 `actions.aerial`), so it states its speed
   *  outright rather than pretending to a windup it never had. */
  speed?: number;
  /** D113: exact launch elevation (rad), overriding the per-button LOFT + aimElev. Carries the header's
   *  timing-quality component, which is an angle, not a power. */
  elev?: number;
}

/** D133 — is the ball in a KEEPER'S HANDS, and is `by` somebody other than that keeper?
 *  D155: takes the slice it reads, not the whole state, so `physics/collide.ts` — which is handed a
 *  `{ ball, players, rng }` view — can reach it through `trapWillRun`. */
export function gkHolding(state: { ball: Pick<BallState, 'possessor'>; players: readonly PlayerState[] }, by: EntityId): boolean {
  if (state.ball.possessor === null || state.ball.possessor === by) return false;
  return state.players[state.ball.possessor]?.action === 'GK_HOLD';
}

function maxSpeedFor(button: number, cfg: SimConfig): number {
  if (button & BTN.SHOOT) return cfg.actions.kick.MAX.SHOOT;
  if (button & BTN.THROUGH) return cfg.actions.kick.MAX.THROUGH;
  return cfg.actions.kick.MAX.PASS;
}
function loftFor(button: number, cfg: SimConfig): number {
  if (button & BTN.SHOOT) return cfg.actions.kick.LOFT.SHOOT;
  if (button & BTN.THROUGH) return cfg.actions.kick.LOFT.THROUGH;
  return cfg.actions.kick.LOFT.PASS;
}

/**
 * D129 — the ONE predicate that decides whether a dead-ball kick is refused, read by `applyKicks`
 * (which drops the kick) and by `applyActions` (which swallows the PRESS so no windup animation ever
 * promises what this will refuse — PT4-P19, and it covers the defending team too). Two sites, one rule,
 * so the swallow and the refusal cannot drift apart.
 *
 * D38 restart discipline: a bystander poking the dead ball simply whiffs. D122: "bystander" is the
 * DEFENDING team — once the whistle has gone the kicking team owns the ball and any of them may play it
 * (IP §6.1 / §22 P13, "quick restart: taker may kick as soon as legality holds"). Only a PENALTY stays
 * taker-only: it is a duel between two named players. D83 ceremony soft-lock: until the whistle even
 * the TAKER's kick is refused — he may walk to the spot but not play it.
 */
export function restartKickRefused(state: MatchState, p: PlayerState, cfg: SimConfig): boolean {
  const r = state.restart;
  if (r === null || (state.phase !== 'KICKOFF' && state.phase !== 'RESTART_PENDING')) return false;
  const takeable = r.kind === 'PENALTY' ? p.id === r.taker : p.team === r.team;
  return !takeable || inCeremony(state, cfg);
}

/**
 * Apply queued kicks to the ball (§5.3 kick table). Runs AFTER integrateBall so the launch velocity
 * is exact (drag begins next tick). Direction = aimYaw; speed = lerp(KICK_MIN, MAX[button],
 * chargeCurve(chargeTicks)); loft raises it by an elevation angle. Ball must be within REACH.
 */
export function applyKicks(state: MatchState, kicks: readonly KickRequest[], cfg: SimConfig, events: SimEvent[]): void {
  const k = cfg.actions.kick;
  const pitch = PITCH[state.mode.teamSize];
  for (const req of kicks) {
    const p = state.players[req.entity];
    if (!p || p.sentOff) continue;
    if (restartKickRefused(state, p, cfg)) continue;
    const b = state.ball;
    // D133: a ball IN A KEEPER'S HANDS is untakeable — the D36 doctrine `resolveTackles` has always
    // enforced, extended to the other two take paths. Without it an opponent's queued kick release fires
    // on a held ball and knocks it downfield; at least one of the clip's two keeper-to-opponent
    // turnovers (ticks 1718–1788) is this, not a distribution decision at all.
    if (gkHolding(state, req.entity)) continue;
    const dist = Math.hypot(b.pos.x - p.pos.x, b.pos.y - p.pos.y);
    if (dist > k.REACH) continue; // not in reach at release

    // D49 auto power: charge no longer scales a kick — PASS/THROUGH power is arrival-calibrated to
    // the receiver (the pass LANDS, whatever the hold), SHOOT always fires at full windup ("maximum
    // intensity that will still hit the goal" — the client solves the elevation). Legacy charge curve
    // stays behind AUTO_POWER=0.
    let chargeTicks = req.chargeTicks;
    let aimYaw = req.aimYaw;
    const isPass = (req.button & (BTN.PASS | BTN.THROUGH)) !== 0;
    const target = isPass ? pickPassTarget(state, req.entity, { x: b.pos.x, y: b.pos.y }, aimYaw, maxSpeedFor(req.button, cfg), cfg) : null;
    if (target && k.AUTO_POWER === 1) {
      const rp = state.players[target.id]!;
      chargeTicks = passChargeFor(Math.hypot(rp.pos.x - b.pos.x, rp.pos.y - b.pos.y), cfg, state.conditions);
    } else if (k.AUTO_POWER === 1 && (req.button & BTN.SHOOT) !== 0 && req.assist !== false) {
      // D49: a PLAYER's shot is always struck full. D55: keeper distributions (assist:false — punts,
      // clears) keep their CALIBRATED charge — auto-full turned every goal kick into a 34 m/s rocket
      // that sailed the length of the pitch ("Abstoß waaay too powerful").
      chargeTicks = k.CHARGE_CAP;
    }
    const curve = Math.pow(Math.min(chargeTicks, k.CHARGE_CAP) / k.CHARGE_CAP, k.CHARGE_EXP);
    const min = (req.button & BTN.SHOOT) !== 0 ? k.SHOOT_MIN : k.KICK_MIN; // D34: tap-shot floor
    let speed = req.speed ?? min + (maxSpeedFor(req.button, cfg) - min) * curve; // D113: a strike states its own force
    let aimPoint: { x: number; y: number } | null = null; // D110: where the pass is MEANT to arrive
    if (target) {
      // D34 soft-lock aim: at where the receiver WILL BE at the TRUE launch speed ("directly at the
      // player, or slightly in front if they move fast"); THROUGH leads THROUGH_LEAD_SEC further along
      // their current running trajectory. D46 assist:false (keeper distributions) keeps the AI's exact
      // aim — power calibration above is not aim bending.
      if (cfg.ai.PASS_ASSIST === 1 && req.assist !== false) {
        const rp = state.players[target.id]!;
        const distNow = Math.hypot(rp.pos.x - b.pos.x, rp.pos.y - b.pos.y);
        // D50: a PASS goes AT the player ("directly at them, or slightly in front") — cap the lead at
        // 0.5 s; the old 1.5 s cap bent long passes to fast runners 30°+ off the line, which read as
        // "the pass just went in a random direction". THROUGH is the lead-the-run verb and keeps its
        // full extra lead along their current trajectory.
        // D112 (HB-C/HB-P8) — THROUGH is now LANE-AWARE. The blind `+THROUGH_LEAD_SEC` extrapolation
        // aimed at wherever the receiver's current velocity happened to point, straight into whoever was
        // standing there; `pickLongTarget` scores a ladder of lead points along the same run and takes
        // the one that actually threads. The RECEIVER is unchanged — it re-uses this very `pickPassTarget`
        // choice — so only the point moves. THIS IS THE ONE DELIBERATE CHANGE TO AN EXISTING VERB.
        // A LOFT+THROUGH is a cross: its ladder is biased into the box in the final third (HB-P4).
        if ((req.button & BTN.THROUGH) !== 0) {
          const long = pickLongTarget(state, req.entity, { x: b.pos.x, y: b.pos.y }, aimYaw, speed, cfg, { box: (req.button & BTN.LOFT) !== 0 });
          aimPoint = long ? { x: long.point.x, y: long.point.y } : { x: rp.pos.x, y: rp.pos.y };
        } else {
          const leadSec = Math.min(distNow / (0.8 * Math.max(speed, 1)), 0.5);
          aimPoint = { x: rp.pos.x + rp.vel.x * leadSec, y: rp.pos.y + rp.vel.y * leadSec };
        }
        aimYaw = Math.atan2(aimPoint.y - b.pos.y, aimPoint.x - b.pos.x);
        // D133 (PT4-P9): a KEEPER's foot pass gets the soft-lock back, so the bend gets D46's guarantee
        // back too — his release can never point into his own half-plane, whatever the receiver choice
        // says. One clamp, applied AFTER the bend (the D62 lesson: clamp first and bend second and the
        // own goal walks back in through the bend).
        if (p.isGk) {
          const fwd = state.attackDir[req.team] > 0 ? 0 : Math.PI;
          let rel = Math.atan2(Math.sin(aimYaw - fwd), Math.cos(aimYaw - fwd));
          const edge = Math.PI / 2 - 1e-3;
          if (rel > edge) rel = edge;
          else if (rel < -edge) rel = -edge;
          aimYaw = fwd + rel;
          aimPoint = { x: b.pos.x + Math.cos(aimYaw), y: b.pos.y + Math.sin(aimYaw) };
        }
      }
    }
    // D137: the D88 overcharge scatter lived here — `aimYaw += (rng·2−1) × (heldTicks − CHARGE_CAP) ×
    // OVERCHARGE_SCATTER`, unbounded, so the clip's 150-tick penalty hold randomised the direction over
    // ±227°. It is deleted with the set-piece manual charge that fed it (§7.3, PT4-P2), and its ONE
    // state.rng draw leaves the §7 order with it — the declared golden re-pin.
    let elev = req.elev ?? loftFor(req.button, cfg) + Math.max(0, Math.min(1, req.aimElev)) * k.ELEV_GAIN;
    // D110 (HB-A/HB-P1) — the HIGH ball. BTN.LOFT held at the moment a PASS/THROUGH fires re-solves the
    // launch as a lofted delivery that LANDS where the ground pass would have arrived: the soft-lock's own
    // meeting point, so the receiver choice and the aim are exactly today's (muscle memory holds) and only
    // the trajectory changes. Gated on bit 17, so every un-lofted kick above is byte-identical to pre-D110.
    // Without a soft-lock point (no teammate at all, or a keeper's assist:false distribution whose aim is
    // deliberately never bent — D46) the modifier degrades to "chip along your own aim at your own power".
    if (isPass && (req.button & BTN.LOFT) !== 0) {
      if (aimPoint !== null) {
        const apex = (req.button & BTN.THROUGH) !== 0 ? k.HIGH.APEX.THROUGH : k.HIGH.APEX.PASS;
        const sol = solveAerialKick(Math.hypot(aimPoint.x - b.pos.x, aimPoint.y - b.pos.y), apex, cfg, state.conditions);
        speed = sol.speed;
        elev = sol.elev;
      } else if (elev < k.HIGH.MIN_ELEV) {
        elev = k.HIGH.MIN_ELEV;
      }
    }
    const horiz = speed * Math.cos(elev);
    b.vel.x = horiz * Math.cos(aimYaw);
    b.vel.y = horiz * Math.sin(aimYaw);
    b.vel.z = speed * Math.sin(elev);
    b.possessor = null;
    b.claimCooldownTicks = cfg.possession.KNOCK_FREE_TICKS; // free window — no instant re-claim by the KICKER (§5.6/D43)
    b.claimCooldownOnly = req.entity; // D43: receivers control the pass immediately
    b.kickedBy = req.entity; // D43: a DELIBERATE kick — pass-follow switching keys on this
    b.lastTouch = { entity: req.entity, team: req.team, tick: state.tick };
    // D119: a kick is a possession change and must SAY so. Until now this site cleared `possessor`
    // silently and emitted nothing unless the kick happened to be a SHOT — so in a log a plain pass
    // was invisible, and the ball simply stopped being anybody's between two POSSESSION events.
    events.push({ t: 'POSSESSION', entity: null, why: 'KICK' });

    // SHOT / shot-pending (§6.2): a goal-ward SHOOT along the LAUNCH ray, on target or within the margin
    b.shotPending = false;
    if (req.button & BTN.SHOOT) emitShot(state, b, req.entity, req.team, pitch, cfg, events);
  }
}

/**
 * Emit SHOT{onTarget} if the LAUNCH ray reaches the shooter's target goal frame (+ SHOT_MARGIN).
 *
 * D130 (§2.2) — the ray is read off `b.vel`, which this function is called immediately after setting, so
 * the event describes the ball that was actually struck. It used to come from `req.aimYaw` and the
 * pre-bend `elev`: the RAW request, before the D34 soft-lock, before the D110 loft solver, and (until
 * D137) before the D88 scatter. That made the log misclassify exactly the failure the playtest hit — the
 * clip's penalty shows no SHOT event at all, which is only diagnostic if the event tracks the launch.
 * There is now one source of truth for where the shot went, and it is the ball's own velocity.
 */
function emitShot(
  state: MatchState,
  b: MatchState['ball'],
  entity: EntityId,
  team: TeamIdx,
  pitch: (typeof PITCH)[keyof typeof PITCH],
  cfg: SimConfig,
  events: SimEvent[],
): void {
  const side = state.attackDir[team];
  const dx = (side * pitch.length) / 2 - b.pos.x;
  const horiz = Math.hypot(b.vel.x, b.vel.y);
  if (horiz < 1e-6) return; // a straight-up knock is not a shot at anything
  const cy = b.vel.x / horiz;
  if (Math.abs(cy) < 1e-6 || Math.sign(cy) !== Math.sign(dx)) return; // not travelling toward that goal
  const t = dx / cy; // horizontal distance along the launch ray to the goal plane
  const yAtGoal = b.pos.y + t * (b.vel.y / horiz);
  const zAtGoal = b.pos.z + t * (b.vel.z / horiz);
  const halfW = pitch.goalWidth / 2;
  const onTarget = Math.abs(yAtGoal) < halfW && zAtGoal >= 0 && zAtGoal < pitch.barH;
  const withinMargin = Math.abs(yAtGoal) < halfW + cfg.rules.SHOT_MARGIN && zAtGoal < pitch.barH + cfg.rules.SHOT_MARGIN;
  if (!onTarget && !withinMargin) return;
  events.push({ t: 'SHOT', entity, onTarget });
  b.shotPending = true;
}
